/**
 * Similar To Brands Filter Component
 *
 * Searchable brand list for style-based similarity filtering.
 * Products with similar style to selected brands will be boosted.
 *
 * Features: Search input, alphabetical sections, checkboxes, scrollable
 */
"use client";

import { brandSearchErrorMessage } from "@/lib/brand-search-state";

import React, { useCallback, useMemo, useRef, useState } from "react";

import { searchBrandNamesByPrefix } from "@/lib/shop-agent-client";
import { colors } from "@/styles/design-tokens";

import { Checkbox } from "./Checkbox";
import { FilterCategoryHeading } from "./FilterCategoryHeading";

// Constants
const DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;
const SEARCH_RESULTS_LIMIT = 25;

// Layout dimensions
const SEARCH_INPUT_HEIGHT = 36;
const SEARCH_ICON_SIZE = 16;
const SEARCH_ICON_LEFT = 10;
const SEARCH_ICON_TOP = 10;
const BRAND_LIST_MAX_HEIGHT = 300;
const BRAND_ROW_HEIGHT = 24;

// Purple accent color for this filter
const ACCENT_COLOR = "#8B5CF6";

export interface BrandOption {
  /** Unique identifier for the brand (typically the brand name) */
  id: string;
  /** Display label for the brand */
  label: string;
}

/** Type guard to validate BrandOption has non-empty strings */
function isValidBrandOption(option: unknown): option is BrandOption {
  return (
    typeof option === "object" &&
    option !== null &&
    "id" in option &&
    "label" in option &&
    typeof (option as BrandOption).id === "string" &&
    typeof (option as BrandOption).label === "string" &&
    (option as BrandOption).id.trim().length > 0 &&
    (option as BrandOption).label.trim().length > 0
  );
}

interface SimilarToBrandsFilterProps {
  brands: BrandOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  isLoading?: boolean;
  className?: string;
}

export function SimilarToBrandsFilter({
  brands,
  selected,
  onChange,
  isLoading = false,
  className = "",
}: SimilarToBrandsFilterProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<BrandOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchRequestIdRef = useRef(0);

  /**
   * Centralized function to reset search state and invalidate pending requests.
   * This prevents race conditions by having a single place that increments the request ID.
   */
  const resetSearchState = useCallback(() => {
    searchRequestIdRef.current += 1;
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
    setSearchError(null);
  }, []);

  // Case-insensitive check if brand is selected
  const isBrandSelected = useCallback(
    (brandId: string) => selected.some((id) => id.toLowerCase() === brandId.toLowerCase()),
    [selected]
  );

  const toggleBrand = useCallback(
    (brandId: string) => {
      // Use case-insensitive comparison for toggle
      const isCurrentlySelected = selected.some((id) => id.toLowerCase() === brandId.toLowerCase());
      const newSelected = isCurrentlySelected
        ? selected.filter((id) => id.toLowerCase() !== brandId.toLowerCase())
        : [...selected, brandId];
      onChange(newSelected);

      // Clear search after selection to show all brands again
      if (searchQuery.trim().length > 0) {
        resetSearchState();
      }
    },
    [selected, onChange, searchQuery, resetSearchState]
  );

  const trimmedQuery = searchQuery.trim();
  const isSearchActive = trimmedQuery.length > 0;

  React.useEffect(() => {
    let cancelled = false;

    // When search becomes inactive, clear results but don't increment requestId
    // (only increment when starting a new search to avoid race conditions)
    if (!isSearchActive) {
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
      return () => {
        cancelled = true;
      };
    }

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(`Type at least ${MIN_QUERY_LENGTH} characters to search`);
      return () => {
        cancelled = true;
      };
    }

    const handle = window.setTimeout(() => {
      // Increment request ID only when actually starting a new search request
      const requestId = ++searchRequestIdRef.current;
      setIsSearching(true);
      setSearchError(null);

      (async () => {
        try {
          const names = await searchBrandNamesByPrefix(trimmedQuery, SEARCH_RESULTS_LIMIT);
          if (cancelled || searchRequestIdRef.current !== requestId) return;

          const seen = new Set<string>();
          const options: BrandOption[] = [];
          for (const rawName of Array.isArray(names) ? names : []) {
            const name = String(rawName || "").trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            const option = { id: name, label: name };
            if (isValidBrandOption(option)) {
              options.push(option);
            }
          }
          setSearchResults(options);
        } catch (error) {
          if (!cancelled && searchRequestIdRef.current === requestId) {
            console.error("SimilarToBrandsFilter prefix search failed:", error);
            setSearchResults([]);
            setSearchError(brandSearchErrorMessage(error));
          }
        } finally {
          if (!cancelled && searchRequestIdRef.current === requestId) {
            setIsSearching(false);
          }
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [isSearchActive, trimmedQuery]);

  const activeBrands = useMemo(() => {
    if (isSearchActive) {
      return searchResults;
    }
    return brands;
  }, [brands, isSearchActive, searchResults]);

  const groupedBrands = useMemo(() => {
    return activeBrands.reduce(
      (acc, brand) => {
        if (!brand?.label) return acc;
        const firstLetter = brand.label[0]?.toUpperCase() || "#";
        if (!acc[firstLetter]) {
          acc[firstLetter] = [];
        }
        acc[firstLetter].push(brand);
        return acc;
      },
      {} as Record<string, BrandOption[]>
    );
  }, [activeBrands]);

  const sortedLetters = useMemo(() => Object.keys(groupedBrands).sort(), [groupedBrands]);
  const noBrandsFound = sortedLetters.length === 0;
  const showNoBrandsState = !isSearching && !searchError && isSearchActive && noBrandsFound;

  return (
    <div className={className}>
      {/* Heading */}
      <FilterCategoryHeading
        label={`STYLE SIMILAR TO (${selected.length})`}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        accentColor={ACCENT_COLOR}
      />

      {/* Description */}
      {isExpanded && (
        <p
          className="text-action-muted text-ink-secondary"
          style={{
            marginTop: "6px",
            marginBottom: "8px",
            fontSize: "10px",
          }}
        >
          Find products with similar style to these brands
        </p>
      )}

      {/* Search Input */}
      {isExpanded && (
        <div>
          <div
            style={{
              width: "100%",
              height: `${SEARCH_INPUT_HEIGHT}px`,
              position: "relative",
              backgroundColor: colors.background.white,
              border: `1px solid ${ACCENT_COLOR}40`,
              borderRadius: "4px",
            }}
          >
            {/* Search Icon */}
            <svg
              width={SEARCH_ICON_SIZE}
              height={SEARCH_ICON_SIZE}
              viewBox="0 0 16 16"
              fill="none"
              style={{
                position: "absolute",
                left: `${SEARCH_ICON_LEFT}px`,
                top: `${SEARCH_ICON_TOP}px`,
              }}
            >
              <circle cx="7" cy="7" r="5" stroke={ACCENT_COLOR} strokeWidth="1.5" />
              <path
                d="M11 11L14 14"
                stroke={ACCENT_COLOR}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>

            {/* Input */}
            <input
              type="text"
              placeholder="Search brands to match style"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="text-action-muted text-ink"
              style={{
                width: "100%",
                height: "100%",
                paddingLeft: "36px",
                paddingRight: "12px",
                border: "none",
                outline: "none",
                backgroundColor: "transparent",
              }}
            />
          </div>

          {/* Brand List */}
          <div
            style={{
              marginTop: "14px",
              maxHeight: `${BRAND_LIST_MAX_HEIGHT}px`,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isLoading && !isSearchActive ? (
              <div className="text-action-muted text-ink-secondary text-center p-5">
                Loading brands...
              </div>
            ) : isSearching ? (
              <div className="text-action-muted text-ink-secondary text-center p-5">Searching…</div>
            ) : searchError ? (
              <div className="text-action-muted text-ink-secondary text-center p-5">
                {searchError}
              </div>
            ) : (
              <>
                {sortedLetters.map((letter) => (
                  <div key={letter}>
                    {/* Letter Header */}
                    <div
                      className="text-action"
                      style={{
                        color: ACCENT_COLOR,
                        marginTop: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      {letter}
                    </div>

                    {/* Brands under this letter */}
                    {groupedBrands[letter].map((brand, index) => (
                      <div
                        key={brand.id}
                        onClick={() => toggleBrand(brand.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          height: `${BRAND_ROW_HEIGHT}px`,
                          marginTop: index > 0 ? "10px" : "0px",
                          cursor: "pointer",
                        }}
                      >
                        {/* Checkbox */}
                        <div
                          style={{
                            paddingTop: "3.996px",
                          }}
                        >
                          <Checkbox
                            checked={isBrandSelected(brand.id)}
                            onChange={() => toggleBrand(brand.id)}
                            accentColor={ACCENT_COLOR}
                          />
                        </div>

                        {/* Label */}
                        <span
                          className={`text-action ${isBrandSelected(brand.id) ? "" : "text-ink"}`}
                          style={{
                            color: isBrandSelected(brand.id) ? ACCENT_COLOR : undefined,
                            marginLeft: "23.988px",
                            paddingTop: "4.654px",
                          }}
                        >
                          {brand.label}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}

                {showNoBrandsState && (
                  <div className="text-action-muted text-ink-secondary text-center p-5">
                    No brands found
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Divider */}
      <div
        style={{
          width: "100%",
          height: "1px",
          backgroundColor: colors.divider.light,
          marginTop: isExpanded ? "15px" : "0px",
        }}
      />
    </div>
  );
}
